import { useTranslation } from 'react-i18next'
import { useToastForReactQuery } from '../../../../utils/custom-hooks'

import styled from '@emotion/styled'
import { useCallback, useMemo } from 'react'
import { DateRangePickerField, SelectField } from '../../../components/fields'
import { Form } from 'react-final-form'
import { Modal } from '@material-ui/core'
import React from 'react'

import dayjs from 'dayjs'
import { DateRangePickerValue } from '../../../components/common/DateRangePicker'
import { Button } from 'react-bootstrap'
import { values } from 'lodash'
import Card from '../../../components/common/Card'
import { useGetDcaRecordReport } from '../../../services/import-dca/import-dca-query'
import {
  EnumImportDcaRecordStatus,
  IExporDcaRecordReportParams,
} from '../../../services/import-dca/import-dca-typed'

const ContentLayout = styled(Card)`
  min-height: 200px;
  padding: 30px 40px 20px 40px;
  grid-row-gap: 12px;
`

const ModalStyled = styled(Modal)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const MAX_DAYS_SPAN = 60
const MAX_MONTHS_SPAN = Math.floor(MAX_DAYS_SPAN / 30)

type ExportDcaRecordFormValues = {
  filterRange: DateRangePickerValue
  status?: BaseOptionType<EnumImportDcaRecordStatus>
}

type ModalExportDcaRecordProps = {
  visible: boolean
  onClose: () => void
}

const ModalExportDcaRecord = (props: ModalExportDcaRecordProps) => {
  const { visible, onClose } = props

  const toast = useToastForReactQuery()
  const { t } = useTranslation()

  const { mutate: dcaRecordReport } = useGetDcaRecordReport()

  const onSubmit = useCallback(
    (values: ExportDcaRecordFormValues) => {
      const createBody: IExporDcaRecordReportParams = {
        startDate: values.filterRange.startDate,
        endDate: values.filterRange.endDate,
        status: values.status?.value,
      }
      dcaRecordReport(createBody, {
        onSuccess: () => {
          toast.open('Success', {
            type: 'success',
            autoClose: 3 * 1000,
          })
          onClose()
        },
        onError: error => {
          toast.open(error, {
            type: 'error',
            autoClose: 3 * 1000,
          })
          onClose()
        },
      })
    },
    [dcaRecordReport, onClose, toast],
  )
  const validateRequired = useCallback((value: string) => (value ? undefined : 'required'), [])

  const statusOptions = useMemo(() => {
    return values(EnumImportDcaRecordStatus).map(status => {
      return {
        label: status.toUpperCase(),
        value: status,
      }
    })
  }, [])

  return (
    <ModalStyled open={visible} onClose={onClose}>
      <div>
        <Form<ExportDcaRecordFormValues>
          onSubmit={onSubmit}
          //initialValues={initialValues}

          subscription={{ dirty: true, submitting: true, valid: true }}
          validate={values => {
            const errors: Partial<Record<keyof ExportDcaRecordFormValues, string>> = {}
            if (values.filterRange) {
              const { startDate, endDate } = values.filterRange
              const monthsDiff = dayjs(endDate).diff(dayjs(startDate), 'month')
              if (Math.abs(monthsDiff) > MAX_MONTHS_SPAN) {
                errors.filterRange = `The start date should be greater that ${MAX_MONTHS_SPAN} months of the end date .`
              }
            }
            return errors
          }}
        >
          {({ handleSubmit, dirty, submitting, valid, form }) => {
            return (
              <form onSubmit={handleSubmit}>
                <ContentLayout>
                  <DateRangePickerField
                    validate={validateRequired}
                    name="filterRange"
                    label="Date Range"
                  />
                  <div style={{ margin: '16px 0px' }}>
                    <SelectField
                      name="status"
                      label="Status"
                      placeholder="Status"
                      options={statusOptions}
                    />
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      padding: '24px',
                      gridColumnGap: '10px',
                    }}
                  >
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={!dirty || submitting || !valid}
                    >
                      Confirm
                    </Button>
                    <Button variant="outline-secondary" onClick={onClose}>
                      Cancel
                    </Button>
                  </div>
                </ContentLayout>
              </form>
            )
          }}
        </Form>
      </div>
    </ModalStyled>
  )
}

export default ModalExportDcaRecord
